import React, { useEffect } from 'react';
import { PerformanceProject, User } from '../types/models';
import { useAriaAnnouncer } from '../hooks/useAriaAnnouncer';

export type UserMode = 'student' | 'teacher' | 'group';

interface ProjectDashboardProps {
  userMode: UserMode;
  userId: string;
  user?: User;
  projects: PerformanceProject[];
  onProjectSelect: (projectId: string) => void;
  onCreateProject: () => void;
  onModeSwitch: (mode: UserMode) => void;
}

export const ProjectDashboard: React.FC<ProjectDashboardProps> = ({
  userMode,
  userId,
  user,
  projects,
  onProjectSelect,
  onCreateProject,
  onModeSwitch,
}) => {
  const { announce } = useAriaAnnouncer();

  useEffect(() => {
    // Announce mode changes to screen readers
    const modeLabels = {
      student: 'Student Solo Mode',
      teacher: 'Teacher Director Mode',
      group: 'Group Ensemble Mode'
    };
    announce(`Switched to ${modeLabels[userMode]}`);
  }, [userMode, announce]);

  return (
    <div className="project-dashboard">
      <header className="dashboard-header">
        <h1>Unified Performing Arts Platform</h1>
        <nav className="mode-switcher" aria-label="User mode selection">
          <button
            className={userMode === 'student' ? 'active' : ''}
            onClick={() => onModeSwitch('student')}
            aria-label="Switch to Student Solo Mode"
            aria-pressed={userMode === 'student'}
          >
            Student Solo
          </button>
          <button
            className={userMode === 'teacher' ? 'active' : ''}
            onClick={() => onModeSwitch('teacher')}
            aria-label="Switch to Teacher Director Mode"
            aria-pressed={userMode === 'teacher'}
            disabled={user?.role !== 'teacher' && user?.role !== 'admin'}
          >
            Teacher Director
          </button>
          <button
            className={userMode === 'group' ? 'active' : ''}
            onClick={() => onModeSwitch('group')}
            aria-label="Switch to Group Ensemble Mode"
            aria-pressed={userMode === 'group'}
          >
            Group Ensemble
          </button>
        </nav>
      </header>

      <main className="dashboard-content">
        {userMode === 'student' && (
          <StudentSoloView
            userId={userId}
            projects={projects}
            onProjectSelect={onProjectSelect}
            onCreateProject={onCreateProject}
          />
        )}
        {userMode === 'teacher' && (
          <TeacherDirectorView
            userId={userId}
            projects={projects}
            onProjectSelect={onProjectSelect}
            onCreateProject={onCreateProject}
          />
        )}
        {userMode === 'group' && (
          <GroupEnsembleView
            userId={userId}
            projects={projects}
            onProjectSelect={onProjectSelect}
          />
        )}
      </main>
    </div>
  );
};

interface ModeViewProps {
  userId: string;
  projects: PerformanceProject[];
  onProjectSelect: (projectId: string) => void;
  onCreateProject?: () => void;
}

const StudentSoloView: React.FC<ModeViewProps> = ({
  userId,
  projects,
  onProjectSelect,
  onCreateProject,
}) => {
  const personalProjects = projects.filter(
    (p) => p.ownerId === userId && p.visibility === 'private'
  );
  const classProjects = projects.filter(
    (p) => p.visibility === 'class' || p.members.some((m) => m.userId === userId)
  );

  return (
    <div className="student-solo-view">
      <section className="practice-dashboard" aria-labelledby="practice-heading">
        <h2 id="practice-heading">My Practice</h2>
        <button onClick={onCreateProject} className="create-project-btn" aria-label="Create new practice project">
          + New Practice Project
        </button>
        <div className="project-list" role="list" aria-label="Personal practice projects">
          {personalProjects.length === 0 ? (
            <p className="empty-state" role="status">No personal practice projects yet</p>
          ) : (
            personalProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onSelect={onProjectSelect}
                showProgress
              />
            ))
          )}
        </div>
      </section>

      <section className="class-projects" aria-labelledby="class-heading">
        <h2 id="class-heading">Class Projects</h2>
        <div className="project-list" role="list" aria-label="Class projects">
          {classProjects.length === 0 ? (
            <p className="empty-state" role="status">No class projects assigned</p>
          ) : (
            classProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onSelect={onProjectSelect}
              />
            ))
          )}
        </div>
      </section>
    </div>
  );
};

const TeacherDirectorView: React.FC<ModeViewProps> = ({
  userId,
  projects,
  onProjectSelect,
  onCreateProject,
}) => {
  const ownedProjects = projects.filter((p) => p.ownerId === userId);
  const collaborativeProjects = projects.filter(
    (p) => p.ownerId !== userId && p.members.some((m) => m.userId === userId && m.role === 'teacher')
  );

  return (
    <div className="teacher-director-view">
      <section className="production-management" aria-labelledby="productions-heading">
        <h2 id="productions-heading">My Productions</h2>
        <button onClick={onCreateProject} className="create-project-btn" aria-label="Create new production">
          + New Production
        </button>
        <div className="project-list" role="list" aria-label="My productions">
          {ownedProjects.length === 0 ? (
            <p className="empty-state" role="status">No productions created yet</p>
          ) : (
            ownedProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onSelect={onProjectSelect}
                showStudentProgress
              />
            ))
          )}
        </div>
      </section>

      <section className="collaborative-projects" aria-labelledby="collaborative-heading">
        <h2 id="collaborative-heading">Collaborative Productions</h2>
        <div className="project-list" role="list" aria-label="Collaborative productions">
          {collaborativeProjects.length === 0 ? (
            <p className="empty-state" role="status">No collaborative projects</p>
          ) : (
            collaborativeProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onSelect={onProjectSelect}
                showStudentProgress
              />
            ))
          )}
        </div>
      </section>
    </div>
  );
};

const GroupEnsembleView: React.FC<ModeViewProps> = ({
  userId,
  projects,
  onProjectSelect,
}) => {
  const sharedProjects = projects.filter(
    (p) => p.visibility !== 'private' && p.members.some((m) => m.userId === userId)
  );

  return (
    <div className="group-ensemble-view">
      <section className="shared-projects" aria-labelledby="shared-heading">
        <h2 id="shared-heading">Shared Productions</h2>
        <div className="project-list" role="list" aria-label="Shared productions">
          {sharedProjects.length === 0 ? (
            <p className="empty-state" role="status">No shared projects available</p>
          ) : (
            sharedProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onSelect={onProjectSelect}
                showGroupStatus
              />
            ))
          )}
        </div>
      </section>
    </div>
  );
};

interface ProjectCardProps {
  project: PerformanceProject;
  onSelect: (projectId: string) => void;
  showProgress?: boolean;
  showStudentProgress?: boolean;
  showGroupStatus?: boolean;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  onSelect,
  showProgress,
  showStudentProgress,
  showGroupStatus,
}) => {
  const completedScenes = project.scenes.filter((scene) =>
    scene.layers.every((layer) => layer.status === 'complete' || layer.status === 'approved')
  ).length;
  const totalScenes = project.scenes.length;

  const ariaLabel = `${project.title}. ${totalScenes} ${totalScenes === 1 ? 'scene' : 'scenes'}. ${
    showProgress ? `${completedScenes} of ${totalScenes} completed.` : ''
  }${showStudentProgress ? `${project.members.length} students.` : ''}${
    showGroupStatus ? `Status: ${completedScenes === totalScenes ? 'Ready' : 'In Progress'}.` : ''
  }`;

  return (
    <article
      className="project-card"
      onClick={() => onSelect(project.id)}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(project.id);
        }
      }}
    >
      <h3>{project.title}</h3>
      {project.metadata.description && (
        <p className="project-description">{project.metadata.description}</p>
      )}
      <div className="project-meta" aria-hidden="true">
        <span className="scene-count">
          {totalScenes} {totalScenes === 1 ? 'scene' : 'scenes'}
        </span>
        {showProgress && (
          <span className="progress">
            {completedScenes}/{totalScenes} completed
          </span>
        )}
        {showStudentProgress && (
          <span className="student-count">{project.members.length} students</span>
        )}
        {showGroupStatus && (
          <span className="integration-status">
            {completedScenes === totalScenes ? 'Ready' : 'In Progress'}
          </span>
        )}
      </div>
    </article>
  );
};
